import { useCallback, useEffect, useRef, useState } from 'react';
import hanko from '../hanko-init';

/**
 * Drives Hanko's `/profile` flow for the two things a person can register on
 * their own account: an authenticator app (2FA) and a passkey.
 *
 * The flow is opened once and parked on `profile_init`. Every action is fired
 * off that captured state, and the resulting state is routed back through the
 * same handler map. A manual `.run()` resolves with the next state rather than
 * dispatching it — the SDK only auto-routes results returned from inside the
 * handler chain — which is why `route` exists.
 *
 * `registered` is read from the flow's own payload (`mfa_config.auth_app_set_up`)
 * and is `null` until that payload arrives. It is never assumed. The panel this
 * replaced printed "your account is protected with 2FA" as fixed text, which
 * was false for every account that never finished enrollment: Hanko asks for a
 * code only when the user actually holds a TOTP secret, so those accounts
 * signed in on a password alone while the screen claimed a second factor.
 */

// NotAllowedError is what the browser raises when a WebAuthn prompt is
// dismissed or times out; it carries no message worth showing.
export function describeFlowError(err) {
    if (!err) return 'Something went wrong. Please try again.';
    if (err.name === 'NotAllowedError') return 'The request was cancelled or timed out.';
    return err.message || 'Something went wrong. Please try again.';
}

export function useProfileSecurity() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState('');          // '' | 'passkey' | 'totp' | 'verify'
    const [enroll, setEnroll] = useState(null);    // { secret, image } while a QR is on screen
    const [code, setCode] = useState('');
    const [notice, setNotice] = useState('');
    const [error, setError] = useState('');

    // The live Hanko flow state. Actions are bound to the state that produced
    // them, so this always holds the most recent one.
    const stateRef = useRef(null);
    const handlersRef = useRef(null);

    const route = useCallback(async (state) => {
        if (!state?.name) return;
        const handler = handlersRef.current?.[state.name];
        if (!handler) {
            setError(`Unexpected response from the sign-in service (${state.name}).`);
            return;
        }
        const next = await handler(state);
        if (next?.name) await route(next);
    }, []);

    useEffect(() => {
        let cancelled = false;

        const handlers = {
            onError: (err) => {
                if (cancelled) return;
                setLoading(false);
                setBusy('');
                setError(describeFlowError(err));
            },
            preflight: (state) => {
                const available = !!window.PublicKeyCredential;
                const probe = (name) => {
                    try {
                        const fn = available && window.PublicKeyCredential[name];
                        return fn ? fn.call(window.PublicKeyCredential) : false;
                    } catch {
                        return false;
                    }
                };
                return Promise.all([
                    Promise.resolve(probe('isUserVerifyingPlatformAuthenticatorAvailable')),
                    Promise.resolve(probe('isConditionalMediationAvailable')),
                ]).then(([platform, conditional]) => state.actions.register_client_capabilities({
                    webauthn_available: available,
                    webauthn_platform_authenticator_available: platform,
                    webauthn_conditional_mediation_available: conditional,
                }).run());
            },
            // The resting state. Reaching it means whatever was in flight has
            // finished, so the QR and the typed code are cleared here.
            profile_init: (state) => {
                stateRef.current = state;
                if (cancelled) return;
                setUser(state.payload?.user || null);
                setEnroll(null);
                setCode('');
                setLoading(false);
            },
            // Re-entered with no payload when a code is rejected, so any QR
            // already on screen is kept rather than blanked.
            mfa_otp_secret_creation: (state) => {
                stateRef.current = state;
                if (cancelled) return;
                if (state.payload?.otp_secret) {
                    setEnroll({
                        secret: state.payload.otp_secret,
                        image: state.payload.otp_image_source || '',
                    });
                }
                setBusy('');
            },
            webauthn_credential_verification: (state) => {
                stateRef.current = state;
                return state.actions?.webauthn_verify_attestation_response?.().run();
            },
            error: (state) => {
                if (cancelled) return;
                setLoading(false);
                setBusy('');
                setError(state?.error?.message || 'Something went wrong. Please try again.');
            },
        };
        handlersRef.current = handlers;

        hanko.flow.init('/profile', handlers).catch((err) => {
            if (cancelled) return;
            setLoading(false);
            setError(describeFlowError(err));
        });

        return () => { cancelled = true; };
    }, []);

    const registerPasskey = useCallback(async () => {
        const state = stateRef.current;
        if (!state?.actions?.webauthn_credential_create) {
            setError('Passkey registration is not available in this browser.');
            return false;
        }
        setError('');
        setNotice('');
        setBusy('passkey');
        try {
            await route(await state.actions.webauthn_credential_create().run());
            setNotice('Passkey registered. You can now sign in with biometrics.');
            return true;
        } catch (err) {
            setError(describeFlowError(err));
            return false;
        } finally {
            setBusy('');
        }
    }, [route]);

    const startEnrollment = useCallback(async () => {
        setError('');
        setNotice('');
        setBusy('totp');
        try {
            let state = stateRef.current;
            // Hanko issues a new secret only when there is no current one, so a
            // re-scan has to drop the old enrollment first. That leaves the
            // account without a second factor until the new code is confirmed,
            // which is what the re-scan confirmation warns about.
            if (!state?.actions?.continue_to_otp_secret_creation && state?.actions?.otp_secret_delete) {
                await route(await state.actions.otp_secret_delete().run());
                state = stateRef.current;
            }
            if (!state?.actions?.continue_to_otp_secret_creation) {
                setError('Two-factor enrollment is not available on this account right now.');
                return;
            }
            await route(await state.actions.continue_to_otp_secret_creation().run());
        } catch (err) {
            setError(describeFlowError(err));
        } finally {
            setBusy('');
        }
    }, [route]);

    const confirmEnrollment = useCallback(async () => {
        const entered = code.replace(/\D/g, '');
        if (entered.length !== 6) {
            setError('Enter the 6-digit code your authenticator app is showing.');
            return false;
        }
        const state = stateRef.current;
        const verify = state?.actions?.otp_code_verify || state?.actions?.otp_code_validate;
        if (!verify) {
            setError('This enrollment has expired. Start the registration again.');
            return false;
        }
        setError('');
        setBusy('verify');
        try {
            const next = await verify({ otp_code: entered }).run();
            // A rejected code comes back as a state carrying an error rather
            // than as a thrown exception, and must not be routed as progress.
            if (next?.error?.message || next?.status >= 400) {
                stateRef.current = next;
                setCode('');
                setError(next?.error?.message || 'That code was not accepted. Wait for the next one and try again.');
                return false;
            }
            await route(next);
            setNotice('Two-factor authentication is registered. You will be asked for a code the next time you sign in with a password.');
            return true;
        } catch (err) {
            setError(describeFlowError(err));
            return false;
        } finally {
            setBusy('');
        }
    }, [code, route]);

    const cancelEnrollment = useCallback(async () => {
        const state = stateRef.current;
        setError('');
        if (!state?.actions?.back) {
            setEnroll(null);
            setCode('');
            return;
        }
        setBusy('totp');
        try {
            await route(await state.actions.back().run());
        } catch (err) {
            setError(describeFlowError(err));
        } finally {
            setBusy('');
        }
    }, [route]);

    return {
        loading,
        busy,
        error,
        notice,
        enroll,
        code,
        setCode,
        setError,
        setNotice,
        // null until the profile payload lands, so callers can tell "not yet
        // known" apart from "known to be absent".
        registered: user ? !!user.mfa_config?.auth_app_set_up : null,
        passkeyCount: user?.passkeys?.length || 0,
        registerPasskey,
        startEnrollment,
        confirmEnrollment,
        cancelEnrollment,
    };
}
